import { useState, useEffect } from "react";
import { Compass, X, Share, PlusSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

const SHOW_DELAY_MS = 30000; // 30 seconds

export function PWAInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Don't show for 7 days after dismissal
    if (dismissedTime && daysSinceDismissed < 7) return;
    
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone;
    if (isStandalone) return;

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.deferredPrompt = e;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show banner after 30 seconds of browsing
    const timer = setTimeout(() => {
      // Only show if we have a deferred prompt (Android) or is iOS
      if (window.deferredPrompt || iOS) {
        setShowBanner(true);
      }
    }, SHOW_DELAY_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      // Show iOS-specific instructions card
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt && !window.deferredPrompt) return;
    
    const prompt = deferredPrompt || window.deferredPrompt;
    prompt.prompt();
    
    const { outcome } = await prompt.userChoice;
    
    if (outcome === 'accepted') {
      // User accepted the install
    }
    
    setDeferredPrompt(null);
    window.deferredPrompt = null;
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  // iOS Instructions Card
  if (showIOSInstructions) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-end justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        onClick={handleDismiss}
        data-testid="ios-install-overlay"
      >
        <div 
          className="w-full max-w-md rounded-2xl p-6 animate-slide-up"
          style={{ backgroundColor: '#0b2821', border: '1px solid rgba(166, 147, 115, 0.3)' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="ios-install-card"
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
            onClick={handleDismiss}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
          
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(166, 147, 115, 0.2)' }}
            >
              <img src="/compass-logo.png" alt="Compass" className="w-10 h-10" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Add Compass to Home</h3>
              <p className="text-white/60 text-sm">For quick access anytime</p>
            </div>
          </div>
          
          {/* Steps */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(166, 147, 115, 0.15)' }}
              >
                <Share className="w-5 h-5" style={{ color: '#a69373' }} />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Tap the Share button</p>
                <p className="text-white/50 text-xs">At the bottom of Safari</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(166, 147, 115, 0.15)' }}
              >
                <PlusSquare className="w-5 h-5" style={{ color: '#a69373' }} />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Add to Home Screen</p>
                <p className="text-white/50 text-xs">Scroll down in the menu</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'rgba(166, 147, 115, 0.15)' }}
              >
                <Compass className="w-5 h-5" style={{ color: '#a69373' }} />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Tap "Add"</p>
                <p className="text-white/50 text-xs">Compass appears on your home screen</p>
              </div>
            </div>
          </div>
          
          {/* Dismiss */}
          <Button
            onClick={handleDismiss}
            className="w-full"
            style={{ backgroundColor: '#a69373', color: '#ffffff' }}
            data-testid="ios-got-it-btn"
          >
            Got it
          </Button>
        </div>
      </div>
    );
  }

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up"
      style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      data-testid="pwa-install-banner"
    >
      <div 
        className="max-w-lg mx-auto rounded-2xl p-4 flex items-center gap-4 shadow-2xl relative"
        style={{ 
          backgroundColor: '#0b2821',
          border: '1px solid rgba(166, 147, 115, 0.3)'
        }}
      >
        {/* Compass Icon */}
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'rgba(166, 147, 115, 0.2)' }}
        >
          <Compass className="w-6 h-6" style={{ color: '#a69373' }} />
        </div>
        
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm">
            {isIOS ? 'Add Compass to your Home Screen' : 'Install Compass for quick access'}
          </p>
          <p className="text-white/50 text-xs mt-0.5">
            {isIOS ? 'Works offline • No app store needed' : 'Installs in seconds • No app store'}
          </p>
        </div>
        
        {/* Install Button */}
        <Button 
          onClick={handleInstall}
          size="sm"
          className="text-xs px-4 shrink-0"
          style={{ backgroundColor: '#a69373', color: '#ffffff' }}
          data-testid="pwa-install-btn"
        >
          {isIOS ? 'How?' : 'Install'}
        </Button>
        
        {/* Close button */}
        <button 
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#1a3a30' }}
          onClick={handleDismiss}
          aria-label="Dismiss"
          data-testid="pwa-dismiss-btn"
        >
          <X className="w-3.5 h-3.5 text-white/60" />
        </button>
      </div>
    </div>
  );
}

export default PWAInstallBanner;
